// @givmo/app-charity — §321 charity-resolution hook (CPA-WEB5).
//
// The §321 statement is READ-ONLY and readable by BOTH Owner AND Member (`charity:read`) — there is no
// role gate on the view (unlike WEB6/WEB7's Owner-gated mutations). But it MUST fail closed while the
// membership read is unresolved: nothing renders (and no §321 read fires) until the caller's ACTIVE org
// is positively resolved. This hook is a thin §321-semantic adapter over the shared active-org context
// (CPA-WEB3) — the statement + grant-drill-down pages read the ACTIVE org here, so a multi-org admin's
// §321 view re-scopes when they switch orgs in the shell, and a single-org admin sees zero change.

import { useActiveOrg } from '../../org/active-org-context';
import type { CharityMembershipOut } from '@givmo/api-client';

export interface CharityResolution {
  /** The resolved ACTIVE-org membership, or null until positively resolved. */
  membership: CharityMembershipOut | null;
  /** The resolved charity id, or null while the membership read is pending/errored/empty. */
  charityId: string | null;
  /** True only once the membership read succeeded AND an active membership exists. */
  isReady: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** Resolve the caller's ACTIVE charity + membership (fail-closed until positively resolved). */
export function useCharity(): CharityResolution {
  const { activeMembership, activeCharityId, isReady, isPending, isError, error, refetch } =
    useActiveOrg();
  return {
    membership: activeMembership,
    charityId: activeCharityId,
    isReady,
    isPending,
    isError,
    error,
    refetch,
  };
}
